import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Toaster } from "react-hot-toast";
import { Outlet, useLocation } from "react-router-dom";
import { debounce } from "lodash";

import { RESUBSCRIBE_BANNER_HEIGHT } from "@/const";
import {
  useResponsiveness,
  useUser,
  useScrollPosition,
} from "@/providers/context";
import { cn } from "@/utils";

import { MobileFooter } from "./components/MobileFooter";
import { NavBar } from "./components/NavBar";
import { ResubscribeBanner } from "./components/ResubscribeBanner";

const shouldSaveScrollPosition = (pathname: string) =>
  pathname === "/" ||
  pathname.startsWith("/content") ||
  pathname.startsWith("/collection");

const getToaster = () => (
  <Toaster
    position="bottom-center"
    containerStyle={{ inset: "36px" }}
    toastOptions={{
      duration: 3000,
      style: {
        background: "#56595D",
        color: "#fff",
        padding: "8px 12px",
        maxWidth: "500px",
        fontSize: "14px",
        fontWeight: "600",
        borderRadius: "8px",
      },
    }}
  />
);

export const PageWithNavBar = () => {
  const { pathname } = useLocation();
  const { isFullscreen, showResubscribeBanner } = useResponsiveness();
  const { uid } = useUser();
  const { getScrollPosition, saveScrollPosition } = useScrollPosition();

  useEffect(() => {
    const scrollContainer = document.getElementById("ScrollContainer--Content");

    if (!scrollContainer) {
      return;
    }

    // Scroll to saved position if there is one, otherwise scroll to top
    const savedScrollPosition = getScrollPosition(pathname);
    scrollContainer.scrollTo(0, savedScrollPosition ?? 0);

    // Save scroll position on scroll if in content tab/subtab/collection
    const handleScroll = debounce(() => {
      saveScrollPosition(pathname, scrollContainer.scrollTop);
    }, 100);

    if (shouldSaveScrollPosition(location.pathname)) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [pathname, getScrollPosition, saveScrollPosition]);

  const fullscreenPortalEl = document.getElementById(
    "fullscreen-player-portal",
  );

  return (
    <>
      {showResubscribeBanner && <ResubscribeBanner />}
      <div
        className={cn(
          "flex min-w-[768px] flex-row overflow-hidden bg-surface-primary",
          !uid && "pb-[78px] sm:pb-0",
        )}
        style={{
          height: showResubscribeBanner
            ? `calc(100% - ${RESUBSCRIBE_BANNER_HEIGHT}px)`
            : "100%",
        }}
      >
        <NavBar />
        <div
          className="h-full w-full overflow-auto"
          id="ScrollContainer--Content"
        >
          <Outlet />
          <MobileFooter />
        </div>
        {isFullscreen && fullscreenPortalEl
          ? createPortal(getToaster(), fullscreenPortalEl)
          : getToaster()}
      </div>
    </>
  );
};
